import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import M from 'materialize-css';
import { compose } from 'redux';
import { firestoreConnect } from 'react-redux-firebase';
import { storage } from '../../config/fbConfig';
import { genString } from '../../utils/StringUtil';
import Form from '@rjsf/material-ui';
import { formSchema, uiSchema } from './schema';
import { actionsContest, timezones, contestTypes } from '../../constants/constants';
import { dateToFullDateTime, stringToDate, dateToMDYHM } from '../../utils/DateUtil';
const refModal = React.createRef();

function NewContest( {auth, firestore, companies, edit, contest} ) {
  const sectionImages = useRef({});
  const [companyId, setCompanyId] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [name, setName] = useState('');
  const [actions, setActions] = useState([]);
  const [contestDate, setContestDate] = useState('');
  const [validDate, setValidDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [timezone, setTimezone] = useState('');
  const [contestType, setContestType] = useState('');
  const [wasAnalyzed, setWasAnalyzed] = useState(false);
  const [active, setActive] = useState(true);
  const [winnerMessage, setWinnerMessage] = useState('');
  const [state, setState] = useState('');
  const [redirectToAllContests, setRedirectToAllContests] = useState(false);
  const [iconUrl, setIconUrl] = useState('');
  const [image, setImage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [formSchemaValue, setFormSchemaValue] = useState({});
  const [sections, setSections] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [currentCompanies, setCurrentCompanies] = useState([]);
  const contestHasFilter = actions.length > 0 && actions[0] === actionsContest.PYN_COLLECTED_FILTERED;

  const setContest = (contest) => {
    setIconUrl(contest.urlIcon);
    setImageUrl(contest.urlImage);
    setName(contest.name);
    setCountryCode(contest.countryCode);
    setContestDate(dateToFullDateTime(contest.contestDate.toDate()));
    setValidDate(dateToFullDateTime(contest.validDate.toDate()));
    setStartDate(dateToFullDateTime(contest.startDate.toDate()));
    setEndDate(dateToFullDateTime(contest.endDate.toDate()));
    setTermsAndConditions(contest.termsAndConditions);
    setWinnerMessage(contest.winnerMessage);
    setActions(contest.actions);
    setTimezone(contest.timezone);
    setContestType(contest.type);
    setWasAnalyzed(contest.wasAnalyzed);
    setActive(contest.active);
    if (contest.pynsFilter) {
      const { uidCompany, state } = contest.pynsFilter;
      if (uidCompany) setCompanyId(uidCompany);
      if (state) setState(state.join(','));
    }
    if (contest.sections) {
      setSections(contest.sections);
      const tr = JSON.stringify({ sections: contest.sections });
      const schemaObj = JSON.parse(tr);
      setFormSchemaValue(schemaObj);
    }
  };

  const filterCompanies = useCallback((country, companyName) => {
    if (!companies) return;
    let filteredCompanies = companies.filter((company) => {
      return company.countryCode === country && (companyName !== '' ? company.companyName.toLowerCase().includes(companyName.toLowerCase()) : true);
    });
    setCurrentCompanies(filteredCompanies);
  }, [companies]);
  
  useEffect(() => M.AutoInit(), [countryCode]);
  
  useEffect(() => {
    if (contest){
      setContest(contest);
      filterCompanies(contest.countryCode, '');
    }
  }, [contest, filterCompanies]);

  const createContest = () => {
    if (!validateContest()) {
      alert('Please fill in all the minimum fields');
      return;
    }
    const newContest = {
      actions,
      updatedAt: firestore.FieldValue.serverTimestamp(),
      countryCode,
      name,
      //TODO remove nameCompany after version 1.0.28(104)
      nameCompany: name,
      contestDate: stringToDate(contestDate),
      validDate: stringToDate(validDate),
      startDate: stringToDate(startDate),
      endDate: stringToDate(endDate),
      formattedStartDate: dateToMDYHM(startDate),
      formattedEndDate: dateToMDYHM(endDate),
      pyns: contest ? contest.pyns : [],
      pynsFilter: getFilter(),
      details: '',
      rules: [],
      sections: getSections(),
      termsAndConditions,
      timezone,
      type: contestType,
      wasAnalyzed,
      active,
      winnerMessage,
    };
    saveContest(newContest);
  };

  const getFilter = () => {
    const filter = {};
    if (!contestHasFilter) {
      return filter;
    }
    if (companyId) {
      filter.uidCompany = companyId;
    }
    if (state) {
      filter.state = state.split(',');
    }
    return filter;
  };

  const getSections = () => {
    const sections = formSchemaValue.sections
      ? formSchemaValue.sections.map((s) => {
        const section = { title: s.title, content: s.content };
        if (s.image) section.image = s.image;
        return section;
      })
      : [];
    return sections;
  };

  const validateContest = () =>
    countryCode.trim() !== '' &&
    name.trim() !== '' &&
    contestDate.trim() !== '' &&
    validDate.trim() !== '' &&
    startDate.trim() !== '' &&
    endDate.trim() !== '' &&
    termsAndConditions.trim() !== '' &&
    actions.length > 0 &&
    timezone.trim() !== '' &&
    contestType.trim() !== '';

  const saveContest = (newContest) => {
    const [query, docId] = generateQuery(newContest);
    query
      .then(() => {
        if (!image && (!formSchemaValue.sections || formSchemaValue.sections.filter((section) => section.file !== undefined).length === 0)) setRedirectToAllContests(true);
        uploadContestImage(image, docId);
        saveSectionsImages(docId);
      })
      .catch((error) => console.error(error));
  };

  const uploadContestImage = (img, docId) => {
    if (img) {
      uploadImage('contests', img, (imageUrl) => {
        firestore.collection('contests').doc(docId).update({ urlImage: imageUrl, urlIcon: imageUrl });
        if (!formSchemaValue.sections || formSchemaValue.sections.filter((section) => section.file !== undefined).length === 0) {
          setRedirectToAllContests(true);
        }
      });
    }
  };

  const updateContestSections = (index, sectionImgUrl, docId) => {
    sectionImages.current[index] = sectionImgUrl;
    const sectionsWithFiles = formSchemaValue.sections ? formSchemaValue.sections.filter((section) => section.file !== undefined) : [];
    const sectionKeys = Object.keys(sectionImages.current);
    if (sectionKeys.length === sectionsWithFiles.length) {
      const sections = formSchemaValue.sections;
      sectionKeys.forEach((key) => {
        sections[key].image = sectionImages.current[key];
        if (sections[key].file) delete sections[key].file;
      });
      firestore.collection('contests').doc(docId).update({ sections });
      setRedirectToAllContests(true);
    }
  };

  const saveSectionsImages = (docId) => {
    if (!formSchemaValue.sections || formSchemaValue.sections.length === 0) return;
    formSchemaValue.sections.forEach((section, index) => {
      if (section.file) uploadSectionImage(index, section.file, (sectionImgUrl) => updateContestSections(index, sectionImgUrl, docId));
    });
  };

  const uploadSectionImage = (index, file, newImageurl) => {
    const newFileName = genString(10);
    const uploadTask = storage.ref(`/contests/sections/${newFileName}`).putString(file, 'data_url');
    uploadTask.on(
      'state_changed',
      (snapShot) => console.log(snapShot),
      (err) => console.error(err),
      () => {
        storage
          .ref('contests/sections')
          .child(newFileName)
          .getDownloadURL()
          .then((fireBaseUrl) => newImageurl(fireBaseUrl));
      },
    );
  };

  const generateQuery = (newContest) => {
    let query = firestore.collection('contests');
    let docId = '';
    if (edit) {
      query = query.doc(contest.id).set(newContest, { merge: true });
      docId = contest.id;
    } else {
      const newRef = query.doc();
      docId = newRef.id;
      query = newRef.set({ id: newRef.id, uid: newRef.id, entriesCount: 0, ...newContest });
    }
    return [query, docId];
  };

  const uploadImage = (folder, file, newImageurl) => {
    const newFileName = genString(5) + file.name;
    const uploadTask = storage.ref(`/${folder}/${newFileName}`).put(file);
    uploadTask.on(
      'state_changed',
      (snapShot) => console.log(snapShot),
      (err) => console.error(err),
      () => {
        storage
          .ref(folder)
          .child(newFileName)
          .getDownloadURL()
          .then((fireBaseUrl) => newImageurl(fireBaseUrl));
      },
    );
  };

  const openCompanyDialog = () => M.Modal.getInstance(refModal.current).open();
  const updateCountry = (country) => {
    setCountryCode(country);
    filterCompanies(country, '');
  };

  const search = (searchTerm) => {
    setSearchName(searchTerm);
    filterCompanies(countryCode, searchTerm);
  };

  const renderCompanyDialog = () => {
    return (
      <div ref={refModal} className="modal">
        <div className="modal-content">
          <h5>Company ID</h5>
          <div className="row">
            <div className="input-field col s12">
              <input id="searchName" type="text" className="validate" value={searchName} onChange={(e) => search(e.target.value)} />
              <label htmlFor="searchName">Company name</label>
            </div>
          </div>
          <div className="search-container">{currentCompanies.map((company) => renderCompany(company))}</div>
        </div>
        <div className="modal-footer">
          <div className="modal-close waves-effect btn btn-flat" onClick={() => search('')}>
            Cancel
          </div>
        </div>
      </div>
    );
  };

  const renderCompany = (company) => (
    <div key={company.id}>
      <div
        onClick={() => {
          setCompanyId(company.uid);
          search('');
        }}
        className="modal-close waves-effect btn btn-flat">
        {company.companyName}
      </div>
    </div>
  );

  const renderImages = () => (
    <div className="row">
      {renderImage(iconUrl, 'Icon')}
      {renderImage(imageUrl, 'Image')}
    </div>
  );

  const renderSectionImages = () => (
    <div style={{ padding: 20 }}>
      <h6>Section images:</h6>
      <div className="row">
        {sections.map((section) => (
          section.image ? renderImage(section.image, section.title) : null
        ))}
      </div>
    </div>
  );

  const renderImage = (source, label) => (
    <div key={label} className="col s4">
      <img src={source} alt="" className="company-logo" />
      <br />
      <small>{label}</small>
    </div>
  );

  const renderCountrySelect = () => (
    <div className="input-field col s6">
      <select className="icons" value={countryCode} onChange={(e) => updateCountry(e.target.value)}>
        <option value="" disabled>
          Select your Country
        </option>
        <option data-icon="images/ca.svg" value="CA">
          Canada
        </option>
        <option data-icon="images/us.svg" value="US">
          United States
        </option>
        <option data-icon="images/bo.svg" value="BO">
          Bolivia
        </option>
      </select>
      <label>Country</label>
    </div>
  );

  const renderContestActionsSelect = () => (
    <div className="input-field col s12">
      <select value={actions.length > 0 ? actions[0] : ''} onChange={(e) => setActions([e.target.value])}>
        <option value="" disabled>
          Choose actions
        </option>
        {Object.keys(actionsContest).map((action) => (
          <option key={action} value={action}>
            {actionsContest[action]}
          </option>
        ))}
      </select>
      <label>Actions</label>
    </div>
  );

  const renderTimezonesSelect = () => (
    <div className="input-field col s6">
      <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
        <option value="" disabled>
          Choose timezone
        </option>
        {timezones.map((timezone) => (
          <option key={timezone} value={timezone}>
            {timezone}
          </option>
        ))}
      </select>
      <label>Timezone</label>
    </div>
  );

  const renderTypesSelect = () => (
    <div className="input-field col s6">
      <select value={contestType} onChange={(e) => setContestType(e.target.value)}>
        <option value="" disabled>
          Choose contest type
        </option>
        {Object.keys(contestTypes).map((type) => (
          <option key={type} value={type}>
            {contestTypes[type]}
          </option>
        ))}
      </select>
      <label>Type</label>
    </div>
  );

  const renderWasAnalyzed = () => (
    <div className="input-field col s6">
      Was analyzed:
      <div className="row">
        <div className="input-field col s6">
          <label>
            <input name="group1" type="radio" checked={!wasAnalyzed} onChange={() => setWasAnalyzed(false)} />
            <span>No</span>
          </label>
        </div>
        <div className="input-field col s6">
          <label>
            <input name="group2" type="radio" checked={wasAnalyzed} onChange={() => setWasAnalyzed(true)} />
            <span>Yes</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderIsActive = () => (
    <div className="input-field col s6">
      Is active:
      <div className="row">
        <div className="input-field col s6">
          <label>
            <input name="group3" type="radio" checked={!active} onChange={() => setActive(false)} />
            <span>No</span>
          </label>
        </div>
        <div className="input-field col s6">
          <label>
            <input name="group4" type="radio" checked={active} onChange={() => setActive(true)} />
            <span>Yes</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderDateTimePicker = (label, field, setter) => (
    <div className="col s6">
      <label htmlFor="contestTime">{label}</label>
      <input type="datetime-local" id="contestTime" name="contestTime" value={field} onChange={(e) =>
        setter(dateToFullDateTime(e.target.value))} />
    </div>
  );

  const renderPynsFilter = () => (
    <div className="col s12" style={{ border: 'solid', borderWidth: 1, borderColor: '#E0E0E0', borderRadius: 5, padding: 10, margin: 10 }}>
      <h5>Pyns filter</h5>
      <div className="row">
        <div className="input-field col s6">
          <div className="col s6">
            <label htmlFor="company_uid">Company ID</label>
            <input id="company_uid" type="text" name="uidCompany" value={companyId} onChange={(e) => setCompanyId(e.target.value)} />
          </div>
          <div className="col s6">
            <div className="btn btn-medium" onClick={() => openCompanyDialog()}>
              Select company
            </div>
          </div>
        </div>
        <div className="input-field col s6">
          <input id="state" type="text" className="validate" value={state} onChange={(e) => setState(e.target.value)} />
          <label htmlFor="state" className="active">
            State
          </label>
        </div>
      </div>
    </div>
  );

  if (!auth.uid) return <Redirect to="/signin" />;
  if (redirectToAllContests) return <Redirect to="/allContest" />;

  return (
    <div className="container">
      <div className="card z-depth-2 project-summary">
        <form className="col s12 new-form">
          <div className="card-content">
            <div className="row">
              <h3>{`${edit ? 'Edit' : 'New'} Contest`}</h3>
            </div>
            {renderImages()}
            <div className="row">
              <div className="input-field col s6">
                <input id="name" type="text" className="validate" value={name} onChange={(e) => setName(e.target.value)} />
                <label htmlFor="name" className="active">
                  Name
                </label>
              </div>
              {renderCountrySelect()}
            </div>
            <div className="row">
              {renderDateTimePicker('Contest date:', contestDate, setContestDate)}
              {renderDateTimePicker('Valid date:', validDate, setValidDate)}
            </div>
            <div className="row">
              {renderDateTimePicker('Start date:', startDate, setStartDate)}
              {renderDateTimePicker('End date:', endDate, setEndDate)}
            </div>
            <div className="row">
              <div className="input-field col s12">
                <textarea id="terms" className="materialize-textarea" value={termsAndConditions} onChange={(e) => setTermsAndConditions(e.target.value)} style={{ marginTop: 10 }} />
                <label htmlFor="terms" className="active">
                  Terms and conditions
                </label>
              </div>
            </div>
            <div className="row">
              <div className="input-field col s12">
                <textarea id="winner" className="materialize-textarea" value={winnerMessage} onChange={(e) => setWinnerMessage(e.target.value)} style={{ marginTop: 10 }} />
                <label htmlFor="winner" className="active">
                  Winner message
                </label>
              </div>
            </div>
            <div className="row">
              {renderTypesSelect()}
              {renderTimezonesSelect()}
            </div>
            <div className="row">
              {renderIsActive()}
              {renderWasAnalyzed()}
            </div>
            <div className="col s12">
              <div className="file-field input-field ">
                <div className="btn">
                  <span>Image</span>
                  <input type="file" onChange={(e) => setImage(e.target.files[0])} />
                </div>
                <br />
                <div className="file-path-wrapper">
                  <input className="file-path validate" type="text" />
                </div>
              </div>
            </div>
            {renderContestActionsSelect()}
            {contestHasFilter && renderPynsFilter()}
          </div>
          {renderCompanyDialog()}
        </form>
        <Form schema={formSchema} uiSchema={uiSchema} children={true} formData={formSchemaValue} onChange={({ formData }) => setFormSchemaValue(formData)} noHtml5Validate />
        {renderSectionImages()}
        <div className="card-action">
          <div className="waves-effect waves-light btn btn-large" onClick={createContest}>
            {edit ? 'Update' : 'Create'}
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    companies: state.firestore.ordered.companies,
    auth: state.firebase.auth,
    contest: ownProps.location.contest,
    edit: ownProps.location.edit,
  };
};
export default compose(firestoreConnect([{ collection: 'companies', orderBy: ['companyName', 'asc'] }]), connect(mapStateToProps))(NewContest);
